import {FetchResult} from "@apollo/client";
import {captureException} from "@sentry/react";
import {differenceInDays, format, parseISO} from "date-fns";
import omit from "lodash/omit";
import upperFirst from "lodash/upperFirst";
import pluralize from "pluralize";
import {TZDate} from "react-day-picker";

import {encodeFilter, withMatchMode} from "@/components/filterDropdown/FilterDropdown.ts";
import {ColumnSpec} from "@/components/table/Table.types.ts";
import {ServiceField} from "@/lib/constants/service.ts";
import {shortDateUser} from "@/lib/format.tsx";
import {AnalyzerTokenUsageInput, AnalyzerTurnType} from "@/lib/queries/analyzerRuns.ts";
import {BulkCreateChecksItemInput, BulkCreateChecksTagInput, CheckFormResult} from "@/lib/queries/checks.ts";
import {AccountUser} from "@/lib/types/accounts.ts";
import {AccountStats} from "@/lib/types/billing.ts";
import {
  AccountServicesUsage,
  CheckCategory,
  CheckTypeDisplay,
  GroupCheckDownCondition,
  MonitoringServiceType,
  ServiceTag,
} from "@/lib/types/checks.ts";
import {GenericMutationResponse, ResponseStatus, ServerErrors} from "@/lib/types/common.ts";
import {MonitoringGroupType} from "@/lib/types/monitoring.ts";
import {TxnScriptStep} from "@/lib/types/transaction.ts";
import {
  ANALYZER_ALERT_PRIORITY_DISPLAY,
  ANALYZER_ALERT_PRIORITY_TYPES,
  ANALYZER_CHECK_FIELD_TITLES,
  ANALYZER_CHECK_TYPE_DEFAULTS,
  ANALYZER_CHECK_TYPE_FAMILIES,
  ANALYZER_CHECK_VALUE_TITLES,
  ANALYZER_COVERAGE_MAX_SURFACES,
  ANALYZER_DEFAULT_LOCATIONS_CAP,
  ANALYZER_ESCALATION_TIERS,
  ANALYZER_GROUP_CHECK_LIMIT_NAME,
  ANALYZER_GROUP_CONDITION_COUNTS,
  ANALYZER_GROUP_CONDITION_PERCENTS,
  ANALYZER_JUSTIFIED_CHECK_TYPES,
  ANALYZER_MARKDOWN_PATTERN,
  ANALYZER_PDF_UNNUMBERED_TITLES,
  ANALYZER_POLL_EDIT_INTRO,
  ANALYZER_REPORT_ALERTS_PRESET,
  ANALYZER_REPORT_BADGE_DISPLAY,
  ANALYZER_REPORT_CHECK_SUMMARY_PRESET,
  ANALYZER_REPORT_CHECKS_PRESET,
  ANALYZER_REPORT_DISCLAIMER,
  ANALYZER_REPORT_DISCLAIMER_PRESET,
  ANALYZER_REPORT_LIKELY_TAG,
  ANALYZER_REPORT_MANUAL_CHECKS_PRESET,
  ANALYZER_REPORT_OVERVIEW_FIELDS,
  ANALYZER_REPORT_OVERVIEW_PRESET,
  ANALYZER_REPORT_SECTION_PRESETS,
  ANALYZER_REPORT_SENSITIVITY_PRESET,
  ANALYZER_REPORT_STATUS_PAGE_PRESET,
  ANALYZER_REPORT_SUMMARY_PRESET,
  ANALYZER_REPORT_TITLE,
  ANALYZER_SENSITIVITY_DEFAULT_ROWS,
  ANALYZER_TAG_NAME_MAX_LENGTH,
  ANALYZER_TIER_LABELS,
  ANALYZER_TOOL_LABELS,
  ANALYZER_USAGE_CHIP_SHOW_RATIO,
} from "@/pages/analyzer/Analyzer.constants.ts";
import {
  AggregatedUsage,
  AlertPriority,
  AnalyzerPhase,
  AnalyzerTagValue,
  AnalyzerUsageChip,
  AnalyzerUsageChipState,
  BusinessFunctionLabel,
  ChatItem,
  ChatItemType,
  ChatToolGroupItem,
  CheckLimitStatus,
  CheckLimitTier,
  CheckLimitTierLookup,
  CheckTypeFamily,
  EscalationTier,
  MonitoringReport,
  NormalizedProposals,
  OrderableCheck,
  ProbeRegion,
  ProposedCheck,
  ProposedCheckColumnField,
  ProposedGroup,
  ReportBusinessFunctionGroup,
  ReportConfidence,
  ReportDisplaySection,
  ReportDisplayTable,
  ReportDomainOverview,
  ReportOnlyCheck,
  ReportPdfPayload,
  ReportPdfRow,
  ReportPdfTable,
  ReportRow,
  ReportRowEmphasis,
  ReportRowField,
  ReportSection,
  ReportSectionType,
  ReportTableVariant,
  ReportTag,
  ReportTypeRationale,
  ReportValue,
  SSECheckSelectionEvent,
  SSEDoneEvent,
  SSEEvent,
  SSEEventType,
  SSEInterviewEvent,
  SSEMessageEvent,
  SSERespondAction,
  SSEStreamBody,
  SSETokenUsage,
  SSEToolResultEvent,
  StatusPage,
} from "@/pages/analyzer/Analyzer.types.ts";
import {
  getCheckMaxInterval,
  getCheckMinInterval,
  getIntervalType,
  IntervalFieldType,
} from "@/pages/checks/components/form/fields/interval/utils.ts";
import {getDefaultMonitoringGroups} from "@/pages/checks/components/form/utils.tsx";
import {QUERY} from "@/pages/checks/constants.ts";

export const normalizeUrl = (value: string): string => {
  const trimmed = value.trim();
  return trimmed.match(/^https?:\/\//) ? trimmed : `https://${trimmed}`;
};

export const isInteractive = (phase: AnalyzerPhase) =>
  phase === AnalyzerPhase.INTERVIEW || phase === AnalyzerPhase.CHECK_SELECTION;

export const parseSSELine = (line: string): SSEEvent | null => {
  const trimmed = line.trim();
  if (!trimmed.startsWith("data: ")) return null;
  try {
    return JSON.parse(trimmed.slice(6)) as SSEEvent;
  } catch {
    return null;
  }
};

export const formatToolLabel = (tool: string, args: Record<string, unknown>, isComplete: boolean): string => {
  const labels = ANALYZER_TOOL_LABELS[tool];
  const label = labels ? (isComplete ? labels.complete : labels.active) : tool;
  const values = labels?.summaryArgs ? labels.summaryArgs.map((key) => args[key]) : Object.values(args);
  const summary = values.filter((value): value is string => typeof value === "string").join(", ");
  return summary ? `${label}: ${summary}` : label;
};

export const formatCost = (usage: SSEDoneEvent["usage"]): string | null => {
  if (!usage.pricing) return null;
  const cost =
    (usage.input_tokens * usage.pricing.input +
      usage.output_tokens * usage.pricing.output +
      (usage.cache_read_tokens ?? 0) * (usage.pricing.cache_read ?? 0) +
      (usage.cache_write_tokens ?? 0) * (usage.pricing.cache_write ?? 0)) /
    1_000_000;
  return `$${cost.toFixed(2)}`;
};

export const getLatestCheckSelectionEvent = (events: SSEEvent[]): SSECheckSelectionEvent | undefined =>
  events.reduce<SSECheckSelectionEvent | undefined>(
    (latest, event) => (event.type === SSEEventType.CHECK_SELECTION ? event : latest),
    undefined
  );

export const hasCheckSelectionEvent = (events: SSEEvent[]): boolean =>
  events.some((event) => event.type === SSEEventType.CHECK_SELECTION);

export const isProposingChecks = (events: SSEEvent[]): boolean =>
  events.some(({type}) => type === SSEEventType.PROPOSING);

export const hasMarkdown = (content: string): boolean => ANALYZER_MARKDOWN_PATTERN.test(content);

const withProposalCount = (event: SSEMessageEvent, events: SSEEvent[], index: number): SSEMessageEvent => {
  if (!event.proposal_summary) {
    return event;
  }
  const selection = events
    .slice(index + 1)
    .find((candidate): candidate is SSECheckSelectionEvent => candidate.type === SSEEventType.CHECK_SELECTION);
  const creatableGroups = (selection?.groups ?? []).filter(({creatable}) => creatable);
  const proposalCount = (selection?.checks.length ?? 0) + creatableGroups.length;
  const noun = pluralize("Check", proposalCount);
  return {...event, content: `I've proposed ${proposalCount} ${noun} covering:\n\n${event.content}`};
};

export const buildChatItems = (events: SSEEvent[], includeSettings = false): ChatItem[] => {
  const items: ChatItem[] = [];
  const resultsByCallId = new Map<string, SSEToolResultEvent>();
  const interviewsByCheckpoint = new Map<string, SSEInterviewEvent>();
  const lastAnswerIndexByCheckpoint = new Map<string, number>();
  const answeredCheckpoints = new Set<string>();
  let lastMessage: SSEMessageEvent | null = null;
  let openGroup: ChatToolGroupItem | null = null;
  let settingsPushed = false;

  events.forEach((event, index) => {
    if (event.type === SSEEventType.TOOL_RESULT) {
      resultsByCallId.set(event.call_id, event);
    } else if (event.type === SSEEventType.ANSWER) {
      lastAnswerIndexByCheckpoint.set(event.checkpoint_id, index);
    }
  });

  events.forEach((event, index) => {
    const keepsGroupOpen =
      event.type === SSEEventType.TOOL_CALL ||
      event.type === SSEEventType.TOOL_RESULT ||
      event.type === SSEEventType.THINKING;
    if (!keepsGroupOpen) {
      openGroup = null;
    }

    switch (event.type) {
      case SSEEventType.MESSAGE: {
        const content = event.content || event.status;
        if (content) {
          lastMessage = event;
          const messageEvent = event.content ? withProposalCount(event, events, index) : {...event, content};
          items.push({type: ChatItemType.MESSAGE, event: messageEvent});
        }
        break;
      }
      case SSEEventType.TOOL_CALL: {
        if (!(event.tool in ANALYZER_TOOL_LABELS)) {
          break;
        }
        if (!openGroup) {
          openGroup = {
            type: ChatItemType.TOOL_GROUP,
            title: lastMessage?.status,
            titleDone: lastMessage?.status_done,
            calls: [],
            isComplete: true,
          };
          items.push(openGroup);
        }
        const result = resultsByCallId.get(event.call_id);
        openGroup.calls.push({call: event, result});
        openGroup.isComplete = openGroup.isComplete && !!result;
        break;
      }
      case SSEEventType.INTERVIEW:
        if (event.checkpoint_id) {
          interviewsByCheckpoint.set(event.checkpoint_id, event);
        }
        if (!event.checkpoint_id || !lastAnswerIndexByCheckpoint.has(event.checkpoint_id)) {
          items.push({type: ChatItemType.POLL, event});
        }
        break;
      case SSEEventType.ANSWER: {
        lastMessage = null;
        const interview = interviewsByCheckpoint.get(event.checkpoint_id);
        if (interview) {
          if (answeredCheckpoints.has(event.checkpoint_id)) {
            items.push({
              type: ChatItemType.MESSAGE,
              event: {type: SSEEventType.MESSAGE, content: ANALYZER_POLL_EDIT_INTRO, timestamp: event.timestamp},
            });
          }
          answeredCheckpoints.add(event.checkpoint_id);
          items.push({
            type: ChatItemType.ANSWER_CHIP,
            event: interview,
            answer: event.answer,
            superseded: lastAnswerIndexByCheckpoint.get(event.checkpoint_id) !== index,
          });
        } else {
          items.push({type: ChatItemType.USER_MESSAGE, content: event.answer});
        }
        break;
      }
      case SSEEventType.CHECK_SELECTION:
        if (includeSettings && !settingsPushed) {
          settingsPushed = true;
          items.push({
            type: ChatItemType.MESSAGE,
            event: {
              type: SSEEventType.MESSAGE,
              content:
                "They'll be created with the Tags and Contacts chosen below — all optional to change here, or per Check by asking me.",
              timestamp: event.timestamp,
            },
          });
          items.push({type: ChatItemType.SETTINGS});
        }
        if (event.outcome) {
          items.push({
            type: ChatItemType.NOTICE,
            success: !!event.success,
            callout: event.success ? "Revision applied" : "Revision not applied",
            body: event.outcome,
          });
        }
        break;
      case SSEEventType.ERROR:
        items.push({type: ChatItemType.ERROR, message: event.message});
        break;
    }
  });

  return items;
};

export const getProposedCheckRowId = (row: ProposedCheck): string => row.id;

export const analyzerCheckColumn = (fieldName: ProposedCheckColumnField): ColumnSpec<ProposedCheck> => ({
  fieldName,
  fieldTitle: ANALYZER_CHECK_FIELD_TITLES[fieldName],
});

export const isBasicCheckType = (usage: AccountServicesUsage | undefined, type: MonitoringServiceType): boolean =>
  usage?.[type]?.check_category === CheckCategory.BASIC;

const byPriority = (a: OrderableCheck, b: OrderableCheck): number =>
  (a.priority ?? Number.MAX_SAFE_INTEGER) - (b.priority ?? Number.MAX_SAFE_INTEGER);

export const isGroupCheck = ({monitoring_service_type}: ReportOnlyCheck): boolean =>
  monitoring_service_type === MonitoringServiceType.GROUP;

export const splitChecksBySection = (
  checks: ProposedCheck[],
  usage: AccountServicesUsage | undefined
): {basic: ProposedCheck[]; advanced: ProposedCheck[]; group: ProposedCheck[]} => ({
  basic: checks.filter((check) => isBasicCheckType(usage, check.monitoring_service_type)),
  advanced: checks.filter((check) => usage?.[check.monitoring_service_type]?.check_category === CheckCategory.ADVANCED),
  group: checks.filter(isGroupCheck),
});

export const buildCheckLimitTierLookup = (
  isBilling2: boolean,
  usage: AccountServicesUsage | undefined,
  stats: AccountStats | undefined
): CheckLimitTierLookup => {
  if (isBilling2) {
    return (type) => {
      if (type === MonitoringServiceType.GROUP) {
        const groupPair = stats?.group_checks;
        if (!groupPair) {
          return null;
        }
        const limitName = ANALYZER_GROUP_CHECK_LIMIT_NAME;
        return {
          tierKey: limitName,
          limitName,
          tierLabel: "Group",
          isBasic: false,
          used: groupPair[0],
          limit: groupPair[1],
        };
      }
      const category = usage?.[type]?.check_category;
      const isBasic = category === CheckCategory.BASIC;
      const pair = isBasic ? stats?.basic_checks : stats?.advanced_checks;
      if (!category || !pair) {
        return null;
      }
      const tierLabel = ANALYZER_TIER_LABELS[category];
      const limitName = `${tierLabel} Check`;
      return {tierKey: limitName, limitName, tierLabel, isBasic, used: pair[0], limit: pair[1]};
    };
  }
  return (type) => {
    const serviceUsage = usage?.[type];
    if (!serviceUsage) return null;
    return {
      tierKey: serviceUsage.limit_field,
      limitName: `${serviceUsage.limit_name} Check`,
      tierLabel: serviceUsage.limit_name,
      isBasic: isBasicCheckType(usage, type),
      limit: serviceUsage.limit,
      used: serviceUsage.used,
    };
  };
};

const getTierRemaining = (tier: CheckLimitTier): number => Math.max(0, tier.limit - tier.used);

const byBasicTierFirst = (a: {isBasic: boolean}, b: {isBasic: boolean}): number =>
  Number(b.isBasic) - Number(a.isBasic);

export const getUsageChips = (
  checks: ProposedCheck[],
  selection: Record<string, boolean>,
  getTier: CheckLimitTierLookup
): AnalyzerUsageChip[] => {
  const byTier = new Map<string, AnalyzerUsageChip>();
  for (const check of checks) {
    const tier = getTier(check.monitoring_service_type);
    if (!tier) continue;
    const chip = byTier.get(tier.tierKey) ?? {
      tierKey: tier.tierKey,
      limitName: tier.limitName,
      tierLabel: tier.tierLabel,
      isBasic: tier.isBasic,
      used: tier.used,
      limit: tier.limit,
      selected: 0,
    };
    if (selection[check.id]) {
      chip.selected += 1;
    }
    byTier.set(tier.tierKey, chip);
  }
  const alwaysShownLabels = Object.values(ANALYZER_TIER_LABELS);
  return [...byTier.values()]
    .filter(
      ({tierLabel, used, selected, limit}) =>
        alwaysShownLabels.includes(tierLabel) || used + selected >= limit * ANALYZER_USAGE_CHIP_SHOW_RATIO
    )
    .sort(byBasicTierFirst);
};

export const getUsageChipState = ({used, selected, limit}: AnalyzerUsageChip): AnalyzerUsageChipState => {
  const total = used + selected;
  if (total > limit) {
    return AnalyzerUsageChipState.OVER;
  }
  return total === limit ? AnalyzerUsageChipState.AT_LIMIT : AnalyzerUsageChipState.UNDER;
};

export const preselectChecks = (checks: ProposedCheck[]): Record<string, boolean> =>
  Object.fromEntries(checks.map(({id}) => [id, true]));

export const carrySelection = (
  previousChecks: ProposedCheck[],
  previousSelection: Record<string, boolean>,
  nextChecks: ProposedCheck[]
): Record<string, boolean> => {
  const previousIds = new Set(previousChecks.map((check) => check.id));
  const selection: Record<string, boolean> = {};
  for (const check of nextChecks) {
    if (previousIds.has(check.id) ? previousSelection[check.id] : true) {
      selection[check.id] = true;
    }
  }
  return selection;
};

export const getUncreatedMemberIds = (check: ProposedCheck, createdByProposalId: Record<string, number>): string[] =>
  (check.group_check_proposal_ids ?? []).filter((memberId) => !createdByProposalId[memberId]);

export const coupleGroupSelection = (
  previous: Record<string, boolean>,
  next: Record<string, boolean>,
  checks: ProposedCheck[],
  createdByProposalId: Record<string, number>
): Record<string, boolean> => {
  const selection = {...next};
  for (const group of checks.filter(isGroupCheck)) {
    if (!selection[group.id]) {
      continue;
    }
    const memberIds = getUncreatedMemberIds(group, createdByProposalId);
    if (!previous[group.id]) {
      memberIds.forEach((memberId) => {
        selection[memberId] = true;
      });
    } else if (memberIds.some((memberId) => !selection[memberId])) {
      delete selection[group.id];
    }
  }
  return selection;
};

export const buildDefaultTagName = (
  domain: string,
  timezone?: string,
  existingLowercaseNames: string[] = []
): string => {
  const now = timezone ? new TZDate(new Date(), timezone) : new Date();
  const prefix = "Assistant - ";
  const buildName = (timestampFormat: string) => {
    const suffix = ` - ${format(now, timestampFormat)}`;
    const hostShortened = domain.slice(0, ANALYZER_TAG_NAME_MAX_LENGTH - prefix.length - suffix.length);
    return prefix + hostShortened + suffix;
  };
  const base = buildName("MMM d");
  return existingLowercaseNames.includes(base.toLowerCase()) ? buildName("MMM d HH:mm:ss") : base;
};

export const getProposalIntervalRange = (
  formData: CheckFormResult,
  check: ProposedCheck
): {min: number; max: number} | null => {
  const serviceDef = formData.serviceDefinitions.find((d) => d.service_type === check.monitoring_service_type);
  if (!serviceDef || getIntervalType(serviceDef.force_interval) !== IntervalFieldType.INPUT_GROUP) {
    return null;
  }
  const frequency = serviceDef.fields.find(({field}) => field === ServiceField.INTERVAL)?.mapped_fields?.frequency;
  return {
    min: getCheckMinInterval(frequency, formData.user.account.limit_check_frequency, formData.mspInterval.min),
    max: getCheckMaxInterval(check.monitoring_service_type),
  };
};

export const canApplyIntervalToProposal = (
  formData: CheckFormResult,
  check: ProposedCheck,
  interval: number
): boolean => {
  const range = getProposalIntervalRange(formData, check);
  return !!range && interval >= range.min && interval <= range.max;
};

export const getBulkIntervalBounds = (
  formData: CheckFormResult,
  checks: ProposedCheck[]
): {min: number; max: number} | null => {
  const ranges: {min: number; max: number}[] = [];
  for (const check of checks) {
    const range = getProposalIntervalRange(formData, check);
    if (range) {
      ranges.push(range);
    }
  }
  if (!ranges.length) {
    return null;
  }
  return {
    min: Math.min(...ranges.map(({min}) => min)),
    max: Math.min(...ranges.map(({max}) => max)),
  };
};

export const getBulkLocationsBound = (formData: CheckFormResult, checks: ProposedCheck[]): number =>
  Math.max(0, ...checks.map((check) => getLocationsForProposal(formData, {...check, num_locations: undefined}).length));

export const getLocationsForProposal = (formData: CheckFormResult, check: ProposedCheck): number[] => {
  const serviceDef = formData.serviceDefinitions.find((d) => d.service_type === check.monitoring_service_type);
  if (!serviceDef) {
    return [];
  }
  return getDefaultMonitoringGroups({
    formData,
    serviceDef,
    requestedCount: check.num_locations,
    locationsCapFallback: ANALYZER_DEFAULT_LOCATIONS_CAP,
  }).map(({id}) => id);
};

export const getHiddenTypes = (formData: CheckFormResult): MonitoringServiceType[] =>
  formData.serviceDefinitions
    .filter(
      (serviceDef) =>
        serviceDef.force_monitoring_group_type === MonitoringGroupType.DEDICATED &&
        getDefaultMonitoringGroups({formData, serviceDef, locationsCapFallback: ANALYZER_DEFAULT_LOCATIONS_CAP})
          .length === 0
    )
    .map(({service_type}) => service_type);

export const isRumCheck = ({monitoring_service_type}: ReportOnlyCheck): boolean =>
  monitoring_service_type === MonitoringServiceType.RUM2;

const buildScriptForProposal = (formData: CheckFormResult, check: ProposedCheck): string | undefined => {
  if (check.monitoring_service_type !== MonitoringServiceType.PAGESPEED) {
    return undefined;
  }
  const serviceDef = formData.serviceDefinitions.find((d) => d.service_type === check.monitoring_service_type);
  const scriptField = serviceDef?.fields.find((f) => f.field === ServiceField.SCRIPT);
  try {
    const steps: TxnScriptStep[] = JSON.parse(scriptField?.default);
    const target = steps.find((step) => step.values && "url" in step.values);
    if (!target?.values) {
      return undefined;
    }
    target.values.url = check.msp_address;
    return JSON.stringify(steps);
  } catch {
    return undefined;
  }
};

const capGroupCondition = (condition: GroupCheckDownCondition, memberCount: number): GroupCheckDownCondition => {
  const count = ANALYZER_GROUP_CONDITION_COUNTS[condition];
  if (count === undefined || count <= memberCount) {
    return condition;
  }
  const capped = Object.values(GroupCheckDownCondition).find(
    (code) => ANALYZER_GROUP_CONDITION_COUNTS[code] === memberCount
  );
  return capped ?? GroupCheckDownCondition.ALL;
};

export const proposalToBulkCheckInput = (
  formData: CheckFormResult,
  check: ProposedCheck,
  createdByProposalId: Record<string, number> = {}
): BulkCreateChecksItemInput => {
  if (isGroupCheck(check)) {
    const memberIds = check.group_check_proposal_ids ?? [];
    const createdGroupId = createdByProposalId[check.id];
    return {
      proposal_id: check.id,
      monitoring_service_type: check.monitoring_service_type,
      name: check.name,
      monitoring_groups: [],
      group_check_proposal_ids: getUncreatedMemberIds(check, createdByProposalId),
      group_check_services: memberIds.flatMap((memberId) => createdByProposalId[memberId] ?? []),
      group_check_down_condition: capGroupCondition(
        check.group_check_down_condition ?? GroupCheckDownCondition.ANY,
        memberIds.length
      ),
      ...(createdGroupId && {group_check_id: createdGroupId}),
    };
  }
  const sensitivity = Number(check.msp_sensitivity);
  const monitoringGroups = getLocationsForProposal(formData, check);
  const script = buildScriptForProposal(formData, check);
  return {
    proposal_id: check.id,
    monitoring_service_type: check.monitoring_service_type,
    name: check.name,
    msp_address: check.msp_address,
    monitoring_groups: monitoringGroups,
    msp_interval: check.msp_interval,
    ...ANALYZER_CHECK_TYPE_DEFAULTS[check.monitoring_service_type],
    ...(sensitivity > 0 &&
      monitoringGroups.length > 0 && {msp_sensitivity: String(Math.min(sensitivity, monitoringGroups.length))}),
    ...(check.msp_port && {msp_port: check.msp_port}),
    ...(check.msp_dns_record_type && {msp_dns_record_type: check.msp_dns_record_type}),
    ...(check.msp_expect_string && {msp_expect_string: check.msp_expect_string}),
    ...(script && {msp_script: script}),
  };
};

export const checksUrlForTags = (tagIds: number[]): string =>
  tagIds.length
    ? `/checks?filters=${encodeFilter(withMatchMode({tags: tagIds.map(String)}, "tags", "AND"))}`
    : "/checks";

const sameNameSet = (a: string[], b: string[]): boolean => a.length === b.length && a.every((name) => b.includes(name));

export const normalizeProposalOverrides = (checks: ProposedCheck[]): NormalizedProposals => {
  let normalized = checks;
  let uniformTags: string[] | undefined;
  let uniformContactGroups: string[] | undefined;
  for (const key of ["tags", "contact_groups"] as const) {
    const overrides = normalized.map((check) => check[key]);
    const first = overrides[0];
    if (!first || !overrides.every((names) => names && sameNameSet(names, first))) {
      continue;
    }
    normalized = normalized.map((check) => omit(check, [key]));
    if (key === "tags") {
      uniformTags = first;
    } else {
      uniformContactGroups = first;
    }
  }
  return {checks: normalized, uniformTags, uniformContactGroups};
};

export const hasDivergentOverrides = (
  checks: ProposedCheck[],
  shared: string[],
  key: "tags" | "contact_groups"
): boolean => {
  const effective = checks.map((check) => check[key] ?? shared);
  return effective.some((names) => !sameNameSet(names, effective[0]));
};

export const buildTagsLockedExample = (
  catalogNames: string[],
  formTagNames: string[],
  checks: ProposedCheck[]
): string => {
  const selected = new Set(formTagNames.map((name) => name.toLowerCase()));
  const tag = catalogNames.find((name) => !selected.has(name.toLowerCase())) ?? formTagNames[0] ?? "A";
  const type = CheckTypeDisplay[checks[0]?.monitoring_service_type ?? MonitoringServiceType.DNS];
  return `Add Tag "${tag}" to all ${type} Checks`;
};

export const buildContactsLockedExample = (groupNames: string[], checks: ProposedCheck[]): string => {
  const name = groupNames.at(-1) ?? "Default";
  const type = CheckTypeDisplay[checks.at(-1)?.monitoring_service_type ?? MonitoringServiceType.DNS];
  return `Add Contact "${name}" to all ${type} Checks`;
};

export const resolveTagInputs = (
  names: string[],
  formTags: AnalyzerTagValue[],
  catalog: ServiceTag[]
): BulkCreateChecksTagInput[] =>
  names.flatMap((name) => {
    const nameLower = name.toLowerCase();
    const matchesName = ({tag}: {tag: string}) => tag.toLowerCase() === nameLower;
    const formTag = formTags.find(matchesName);
    if (formTag) {
      return [formTag.id ? {id: formTag.id} : {tag: formTag.tag, color_hex: formTag.color_hex}];
    }
    const catalogTag = catalog.find(matchesName);
    return catalogTag ? [{id: catalogTag.id}] : [];
  });

export const resolveContactGroupIds = (names: string[], groups: {id: number; name: string}[]): number[] =>
  names.flatMap((name) => {
    const nameLower = name.toLowerCase();
    const group = groups.find((entry) => entry.name.toLowerCase() === nameLower);
    return group ? [group.id] : [];
  });

export const resolveContactGroupNames = (ids: number[], groups: {id: number; name: string}[]): string[] =>
  ids.flatMap((id) => groups.find((entry) => entry.id === id)?.name ?? []);

export const flattenServerErrors = (errors: ServerErrors): string =>
  Object.entries(errors)
    .flatMap(([field, messages]) => {
      const prefix = field === "__all__" ? "" : `${ANALYZER_CHECK_FIELD_TITLES[field] ?? field}: `;
      return messages.map((message) => `${prefix}${message.replace(/<[^>]+>/g, "")}`);
    })
    .join(" ");

export const getCheckLimitStatuses = (
  checks: ProposedCheck[],
  selection: Record<string, boolean>,
  getTier: CheckLimitTierLookup
): CheckLimitStatus[] => {
  const byTier = new Map<string, CheckLimitStatus>();
  for (const check of checks) {
    const tier = getTier(check.monitoring_service_type);
    if (!tier) continue;
    const status = byTier.get(tier.tierKey) ?? {
      tierKey: tier.tierKey,
      limitName: tier.limitName,
      isBasic: tier.isBasic,
      limit: tier.limit,
      remaining: getTierRemaining(tier),
      proposed: 0,
      selected: 0,
    };
    status.proposed += 1;
    if (selection[check.id]) {
      status.selected += 1;
    }
    byTier.set(tier.tierKey, status);
  }
  return [...byTier.values()].sort(byBasicTierFirst);
};

export const addUsages = (base: AggregatedUsage | null, usage: SSETokenUsage, elapsed = 0): AggregatedUsage => ({
  elapsed: (base?.elapsed ?? 0) + elapsed,
  input_tokens: (base?.input_tokens ?? 0) + usage.input_tokens,
  output_tokens: (base?.output_tokens ?? 0) + usage.output_tokens,
  cache_read_tokens: (base?.cache_read_tokens ?? 0) + (usage.cache_read_tokens ?? 0),
  cache_write_tokens: (base?.cache_write_tokens ?? 0) + (usage.cache_write_tokens ?? 0),
  pricing: usage.pricing ?? base?.pricing,
});

const toDisplaySection = ({type, intro, rows}: ReportSection): ReportDisplaySection => ({
  ...ANALYZER_REPORT_SECTION_PRESETS[type],
  intro,
  rows,
});

const formatExpiry = (expiresAt: string, user: AccountUser): string => {
  const expiresAtDate = parseISO(expiresAt);
  const now = new Date();
  const date = shortDateUser(expiresAt, user);
  if (expiresAtDate < now) {
    return `expired ${date}`;
  }
  const days = differenceInDays(expiresAtDate, now);
  const remaining = days === 0 ? "today" : `${days.toLocaleString()} ${pluralize("day", days)}`;
  return `expires ${date} (${remaining})`;
};

const toOverviewSecondary = ({secondary, expires_at}: ReportValue, user: AccountUser): string | undefined => {
  const parts: string[] = [];
  if (secondary) {
    parts.push(secondary);
  }
  if (expires_at) {
    parts.push(formatExpiry(expires_at, user));
  }
  return parts.length > 0 ? parts.join(" · ") : undefined;
};

const toOverviewSection = (domainOverview: ReportDomainOverview, user: AccountUser): ReportDisplaySection => ({
  ...ANALYZER_REPORT_OVERVIEW_PRESET,
  rows: [
    {label: "Domain", value: domainOverview.domain},
    ...ANALYZER_REPORT_OVERVIEW_FIELDS.flatMap(({field, label}) => {
      const reportValue = domainOverview[field];
      if (!reportValue) {
        return [];
      }
      return [{label, value: reportValue.value, secondary: toOverviewSecondary(reportValue, user)}];
    }),
  ],
});

const conjunctionList = new Intl.ListFormat("en", {type: "conjunction"});
const disjunctionList = new Intl.ListFormat("en", {type: "disjunction"});

// Lowercases a label for use mid-sentence while keeping acronyms such as MSP, SSO or IT
const lowercaseWords = (label: string): string =>
  label.replace(/\b[A-Z][a-z]+\b/g, (word) => word.toLowerCase()).replace(" & ", " and ");

const matchesLabel = (label: string, fixedLabel: BusinessFunctionLabel): boolean =>
  lowercaseWords(fixedLabel).startsWith(lowercaseWords(label));

const uniqueTypes = (checks: (ProposedCheck | ReportOnlyCheck)[]): Set<MonitoringServiceType> =>
  new Set(checks.map(({monitoring_service_type}) => monitoring_service_type));

const normalizeSurfaceUrl = (url: string): string =>
  url
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\/(www\.)?/, "")
    .replace(/\/+$/, "");

const toProseLabel = (label: string): string =>
  /^[A-Z][a-z]/.test(label) ? label.charAt(0).toLowerCase() + label.slice(1) : label;

const monitoredSurfaces = (proposals: ProposedCheck[], sections: ReportSection[]): string[] => {
  const rows = sections.find(({type}) => type === ReportSectionType.SITE_ARCHITECTURE)?.rows ?? [];
  const labelByUrl = new Map(rows.map(({label, value}) => [normalizeSurfaceUrl(value), toProseLabel(label)]));
  const labels = proposals
    .filter(({monitoring_service_type}) => monitoring_service_type === MonitoringServiceType.HTTP)
    .sort(byPriority)
    .map(({msp_address}) => labelByUrl.get(normalizeSurfaceUrl(msp_address)))
    .filter((label) => label !== undefined);
  return [...new Set(labels)];
};

const toCoverageSentence = (
  proposals: ProposedCheck[],
  reportOnlyChecks: ReportOnlyCheck[],
  sections: ReportSection[]
): string => {
  const total = proposals.length + reportOnlyChecks.length;
  if (total === 0) {
    return "";
  }
  const typeCount = uniqueTypes([...proposals, ...reportOnlyChecks]).size;
  const split =
    reportOnlyChecks.length > 0
      ? `: ${proposals.length} the Assistant can create now and ${reportOnlyChecks.length} to set up manually`
      : "";
  const count = `This plan recommends ${total} ${pluralize("Check", total)} across ${typeCount} ${pluralize("type", typeCount)}${split}.`;

  const surfaceItems = monitoredSurfaces(proposals, sections).slice(0, ANALYZER_COVERAGE_MAX_SURFACES);
  const familyTypes = uniqueTypes([...proposals, ...reportOnlyChecks]);
  if (surfaceItems.length > 0) {
    familyTypes.delete(MonitoringServiceType.HTTP);
  }
  const families = Object.values(CheckTypeFamily).filter((family) =>
    ANALYZER_CHECK_TYPE_FAMILIES[family].some((type) => familyTypes.has(type))
  );

  const priorities: string[] = [];
  if (surfaceItems.length > 0) {
    priorities.push(`availability of the ${conjunctionList.format(surfaceItems)}`);
  }
  if (families.length > 0) {
    const familyList = conjunctionList.format(families);
    priorities.push(surfaceItems.length > 0 ? `plus ${familyList}` : familyList);
  }
  if (priorities.length === 0) {
    return count;
  }
  return `${count} It prioritizes ${priorities.join(", ")}.`;
};

const groupTypesByTier = (
  types: MonitoringServiceType[],
  usage: AccountServicesUsage | undefined
): {label: string; types: MonitoringServiceType[]}[] =>
  [...Object.values(CheckCategory), null]
    .map((category) => ({
      label: category ? ANALYZER_TIER_LABELS[category] : "",
      types: types.filter((type) => (usage?.[type]?.check_category ?? null) === category),
    }))
    .filter(({types: tierTypes}) => tierTypes.length > 0);

export const getManualRowId = (index: number): string => `manual:${index}`;

export const orderChecks = <T extends OrderableCheck>(
  checks: T[],
  usage: AccountServicesUsage | undefined,
  getPosition: (check: T) => number | undefined = () => undefined
): T[] => {
  const byPosition = (a: T, b: T): number =>
    (getPosition(a) ?? Number.MAX_SAFE_INTEGER) - (getPosition(b) ?? Number.MAX_SAFE_INTEGER) || byPriority(a, b);
  const sorted = [...checks].sort(byPosition);
  return groupTypesByTier([...uniqueTypes(sorted)], usage)
    .flatMap(({types: tierTypes}) => tierTypes)
    .flatMap((type) => sorted.filter(({monitoring_service_type}) => monitoring_service_type === type));
};

export const assignPositions = (previous: Map<string, number>, orderedIds: string[]): Map<string, number> => {
  const next = new Map(previous);
  let last = Math.max(0, ...next.values());
  for (const id of orderedIds) {
    if (!next.has(id)) {
      last += 1;
      next.set(id, last);
    }
  }
  return next;
};

const toCheckTables = (
  ordered: ReportOnlyCheck[],
  typeRationales: ReportTypeRationale[],
  positionByCheck: Map<ReportOnlyCheck, number>
): ReportDisplayTable[] =>
  [...uniqueTypes(ordered)].map((type) => {
    const isTransaction = type === MonitoringServiceType.TRANSACTION;
    const valueTitle = ANALYZER_CHECK_VALUE_TITLES[type] ?? ANALYZER_CHECK_FIELD_TITLES.msp_address;
    const justificationTitle = ANALYZER_JUSTIFIED_CHECK_TYPES.includes(type)
      ? "Justification"
      : ANALYZER_CHECK_FIELD_TITLES.justification;
    return {
      title: `${CheckTypeDisplay[type]} Checks`,
      variant: ReportTableVariant.CHECKS,
      rationale: typeRationales.find((typeRationale) => typeRationale.type === type)?.rationale,
      columns: [
        {field: ReportRowField.POSITION, title: "#"},
        {field: ReportRowField.LABEL, title: ANALYZER_CHECK_FIELD_TITLES.name},
        {field: ReportRowField.VALUE, title: valueTitle},
        {field: ReportRowField.SECONDARY, title: justificationTitle},
      ],
      rows: ordered
        .filter(({monitoring_service_type}) => monitoring_service_type === type)
        .map((check) => {
          const {name, msp_address, steps, justification, confidence} = check;
          const value = isTransaction && steps ? steps : msp_address;
          return {position: positionByCheck.get(check), label: name, value, secondary: justification, confidence};
        }),
    };
  });

const toManualChecksSections = (
  ordered: ReportOnlyCheck[],
  typeRationales: ReportTypeRationale[],
  positionByCheck: Map<ReportOnlyCheck, number>
): ReportDisplaySection[] => {
  if (ordered.length === 0) {
    return [];
  }
  return [
    {
      ...ANALYZER_REPORT_MANUAL_CHECKS_PRESET,
      intro: "The Assistant cannot create these Check types yet. Set them up from ",
      link: {
        label: "the Checks page.",
        to: `/checks/create?${QUERY.MONITORING_SERVICE_TYPE}=${ordered[0].monitoring_service_type}`,
      },
      rows: [],
      tables: toCheckTables(ordered, typeRationales, positionByCheck),
    },
  ];
};

const groupByBusinessFunction = (checks: ReportOnlyCheck[]): ReportBusinessFunctionGroup[] => {
  const groups = new Map<string, ReportBusinessFunctionGroup>();
  for (const check of checks) {
    const {business_function, monitoring_service_type} = check;
    const businessFunction = business_function?.trim();
    const label = businessFunction || CheckTypeDisplay[monitoring_service_type];
    const key = label.toLowerCase();
    const group = groups.get(key);
    if (group) {
      group.checks.push(check);
    } else {
      groups.set(key, {label, tagged: Boolean(businessFunction), checks: [check]});
    }
  }
  return [...groups.values()];
};

const formatPositions = (positions: number[]): string => {
  const sorted = [...positions].sort((left, right) => left - right);
  const ranges: [number, number][] = [];
  for (const position of sorted) {
    const last = ranges.at(-1);
    if (last && last[1] === position - 1) {
      last[1] = position;
    } else {
      ranges.push([position, position]);
    }
  }
  return ranges.map(([start, end]) => (start === end ? `#${start}` : `#${start}-${end}`)).join(", ");
};

export const describeGroupCondition = (condition: GroupCheckDownCondition, memberCount: number): string => {
  if (condition === GroupCheckDownCondition.ALL) {
    return "Alert if all Checks in the group fail";
  }
  const percent = ANALYZER_GROUP_CONDITION_PERCENTS[condition];
  const count = ANALYZER_GROUP_CONDITION_COUNTS[condition];
  const threshold = percent !== undefined ? (memberCount * percent) / 100 : Math.min(count ?? 1, memberCount);
  const downCount = Math.max(1, Math.ceil(threshold));
  if (downCount <= 1) {
    return "Alert if any Check in the group fails";
  }
  return `Alert if ${downCount}+ Checks fail simultaneously`;
};

const formatMembers = <T>(checks: T[], getPosition: (check: T) => number | undefined): string =>
  `Checks ${formatPositions(checks.map((check) => getPosition(check) ?? 0))}`;

export const toGroupProposal = (
  group: ProposedGroup,
  proposals: ProposedCheck[],
  positionById: Map<string, number>
): ProposedCheck => {
  const {id, label, name, condition, member_ids, tags, contact_groups} = group;
  const members = proposals.filter((check) => member_ids.includes(check.id));
  return {
    id,
    monitoring_service_type: MonitoringServiceType.GROUP,
    name,
    business_function: label,
    msp_address: formatMembers(members, (member) => positionById.get(member.id)),
    justification: describeGroupCondition(condition, members.length),
    group_check_proposal_ids: members.map((member) => member.id),
    group_check_down_condition: condition,
    ...(tags && {tags}),
    ...(contact_groups && {contact_groups}),
  };
};

const toGroupReportRow = (
  {name, label, condition}: ProposedGroup,
  rows: ReportOnlyCheck[],
  positionByCheck: Map<ReportOnlyCheck, number>
): ReportOnlyCheck => ({
  monitoring_service_type: MonitoringServiceType.GROUP,
  name,
  business_function: label,
  msp_address: formatMembers(rows, (check) => positionByCheck.get(check)),
  justification: describeGroupCondition(condition, rows.length),
});

const isMainSite = (domain: string, {msp_address}: ProposedCheck): boolean =>
  normalizeSurfaceUrl(msp_address) === domain.toLowerCase();

const alertPriorityByType = (type: MonitoringServiceType): AlertPriority => {
  const entries = Object.entries(ANALYZER_ALERT_PRIORITY_TYPES) as [AlertPriority, MonitoringServiceType[]][];
  const match = entries.find(([, types]) => types.includes(type));
  return match ? match[0] : AlertPriority.P3;
};

const toPriorityByCheck = (
  domain: string,
  proposals: ProposedCheck[],
  reportOnlyChecks: ReportOnlyCheck[]
): Map<ReportOnlyCheck, AlertPriority> => {
  const priorityByCheck = new Map<ReportOnlyCheck, AlertPriority>();
  const httpChecks = proposals
    .filter(({monitoring_service_type}) => monitoring_service_type === MonitoringServiceType.HTTP)
    .sort(byPriority);
  const p1Count = Math.ceil(httpChecks.length / 3);
  httpChecks.forEach((check, index) => {
    const isP1 = index < p1Count || isMainSite(domain, check);
    priorityByCheck.set(check, isP1 ? AlertPriority.P1 : AlertPriority.P2);
  });
  for (const check of proposals) {
    if (check.monitoring_service_type === MonitoringServiceType.HTTP || isGroupCheck(check)) {
      continue;
    }
    const isEmailRecord =
      check.monitoring_service_type === MonitoringServiceType.DNS &&
      ["TXT", "MX"].includes(check.msp_dns_record_type ?? "");
    priorityByCheck.set(check, isEmailRecord ? AlertPriority.P2 : alertPriorityByType(check.monitoring_service_type));
  }
  for (const check of reportOnlyChecks) {
    if (!isGroupCheck(check)) {
      priorityByCheck.set(check, alertPriorityByType(check.monitoring_service_type));
    }
  }
  return priorityByCheck;
};

const toPriorityCells = (
  groups: ReportBusinessFunctionGroup[],
  priorityByCheck: Map<ReportOnlyCheck, AlertPriority>,
  positionByCheck: Map<ReportOnlyCheck, number>,
  priority: AlertPriority
): string[] =>
  groups.flatMap(({label, checks}) => {
    const positions = checks
      .filter((check) => priorityByCheck.get(check) === priority)
      .map((check) => positionByCheck.get(check) ?? 0);
    return positions.length > 0 ? [`${label} (${formatPositions(positions)})`] : [];
  });

const toAlertSection = (
  groups: ReportBusinessFunctionGroup[],
  priorityByCheck: Map<ReportOnlyCheck, AlertPriority>,
  positionByCheck: Map<ReportOnlyCheck, number>,
  alertNote: string
): ReportDisplaySection => {
  const functions = groups.filter(({tagged}) => tagged).map(({label}) => lowercaseWords(label));
  const affectedAreas = functions.length > 0 ? `: ${disjunctionList.format(functions)}` : "";
  const priorityRows = Object.values(AlertPriority).flatMap((priority) => {
    const cells = toPriorityCells(groups, priorityByCheck, positionByCheck, priority);
    if (cells.length === 0) {
      return [];
    }
    const {label, escalation} = ANALYZER_ALERT_PRIORITY_DISPLAY[priority];
    return [{label, value: cells.join(", "), secondary: escalation}];
  });
  return {
    ...ANALYZER_REPORT_ALERTS_PRESET,
    rows: [],
    tables: [
      {
        title: "Escalation Tiers",
        variant: ReportTableVariant.TIERS,
        columns: [
          {field: ReportRowField.LABEL, title: "Tier"},
          {field: ReportRowField.VALUE, title: "Wait Time"},
          {field: ReportRowField.SECONDARY, title: "Contacts"},
          {field: ReportRowField.DETAIL, title: "Channels"},
        ],
        rows: ANALYZER_ESCALATION_TIERS.map(({tier, waitTime, channels, contacts}) => ({
          label: tier,
          value: waitTime,
          secondary: tier === EscalationTier.TIER_2 ? `${contacts}${affectedAreas}` : contacts,
          detail: channels,
        })),
      },
      {
        title: "Priority Classification",
        variant: ReportTableVariant.PRIORITY,
        columns: [
          {field: ReportRowField.LABEL, title: "Priority"},
          {field: ReportRowField.VALUE, title: "Checks"},
          {field: ReportRowField.SECONDARY, title: "Escalation Speed"},
        ],
        rows: priorityRows,
        note: `Note: ${alertNote}`,
      },
    ],
  };
};

const formatRange = (values: number[]): string => {
  const min = Math.min(...values);
  const max = Math.max(...values);
  return min === max ? String(min) : `${min}-${max}`;
};

const toSensitivitySection = (
  proposals: ProposedCheck[],
  reportOnlyChecks: ReportOnlyCheck[],
  probeRegions: ProbeRegion[],
  probeRegionsReason: string,
  formData: CheckFormResult | undefined
): ReportDisplaySection => {
  const [primaryRegion, ...otherRegions] = probeRegions;
  const reason = probeRegionsReason
    .trim()
    .replace(/^because\s+/i, "")
    .replace(/\.$/, "");
  const validators = upperFirst(conjunctionList.format([...otherRegions, "fallback"]));
  const regionsNote =
    `${primaryRegion} probe coverage should be prioritized because ${reason}. ` +
    `${validators} probes add useful external validation and help identify CDN, DNS or route-specific issues.`;
  const present = uniqueTypes([...proposals, ...reportOnlyChecks]);
  const rows = ANALYZER_SENSITIVITY_DEFAULT_ROWS.filter(({types}) => types.some((type) => present.has(type))).map(
    (row) => {
      const {label, types, sensitivity, retries} = row;
      if ("probes" in row) {
        return {label, value: row.probes, secondary: sensitivity, detail: retries};
      }
      const {locations} = row;
      const created = proposals.filter(({monitoring_service_type}) => types.includes(monitoring_service_type));
      const counts = created.map(({num_locations}) => num_locations).filter((count) => count !== undefined);
      const count = counts.length > 0 ? formatRange(counts) : String(locations);
      const maxCount = counts.length > 0 ? Math.max(...counts) : locations;
      const sensitivities = new Set(created.map(({msp_sensitivity}) => msp_sensitivity).filter(Boolean));
      const createdSensitivity =
        sensitivities.size === 1 && new Set(counts).size === 1
          ? `${[...sensitivities][0]} of ${count} must fail`
          : sensitivity;
      const serviceDef = formData?.serviceDefinitions.find(({service_type}) => types.includes(service_type));
      const defaultRetries = serviceDef?.fields.find(({field}) => field === ServiceField.NUM_RETRIES)?.default;
      const createdRetries =
        created.length > 0 && typeof defaultRetries === "number"
          ? `${defaultRetries} ${pluralize("retry", defaultRetries)}`
          : retries;
      const regions = conjunctionList.format([...probeRegions.slice(0, maxCount - 1), "a fallback region"]);
      return {
        label,
        value: `${count} locations across ${regions}`,
        secondary: createdSensitivity,
        detail: createdRetries,
      };
    }
  );
  return {
    ...ANALYZER_REPORT_SENSITIVITY_PRESET,
    rows: [],
    tables: [
      {
        columns: [
          {field: ReportRowField.LABEL, title: "Check Type"},
          {field: ReportRowField.VALUE, title: "Probe Locations"},
          {field: ReportRowField.SECONDARY, title: "Sensitivity"},
          {field: ReportRowField.DETAIL, title: "Retries"},
        ],
        rows,
        note: regionsNote,
      },
    ],
  };
};

const toStatusPageSection = ({stakeholders, existing_host, components}: StatusPage): ReportDisplaySection => {
  const roles = lowercaseWords(stakeholders.trim().replace(/\.$/, ""));
  const audience = roles.endsWith("stakeholders") ? roles : `${roles} stakeholders`;
  const intro = existing_host
    ? `Keep the existing status page at ${existing_host} for customers and add a private internal page for ${audience}.`
    : `Create a private internal status page for ${audience}.`;
  const sharedLabels = [BusinessFunctionLabel.EMAIL_DOMAIN_TRUST, BusinessFunctionLabel.INFRASTRUCTURE_DEPENDENCIES];
  const isShared = (name: string) => sharedLabels.some((fixedLabel) => matchesLabel(name, fixedLabel));
  const separated = components.filter(({name}) => !isShared(name)).map(({name}) => lowercaseWords(name));
  const optional =
    `A lightweight public status page is optional, but an internal page is useful for separating ` +
    `${[...separated, "email and hosting/provider incidents"].join(", ")}.`;
  const incidents = components
    .filter(({name}) => !isShared(name) && !matchesLabel(name, BusinessFunctionLabel.PUBLIC_WEBSITE_HEALTH))
    .map(({name}) => {
      // "an" before a vowel or an acronym whose first letter is said with one: an MSP, an SSO, an IT
      const article = /^(?:[aeiouAEIOU]|[FHLMNRSX](?=[A-Z]))/.test(name.trim()) ? "an" : "a";
      return `${article} ${lowercaseWords(name)} issue`;
    });
  const incidentList = disjunctionList.format([...incidents, "an email or provider problem", "a broad website outage"]);
  const note =
    `Separating these components helps the team avoid ambiguous incident language. ` +
    `${upperFirst(incidentList)} should have different owners and escalation paths.`;
  return {
    ...ANALYZER_REPORT_STATUS_PAGE_PRESET,
    intro: `${intro} ${optional}`,
    rows: [],
    tables: [
      {
        title: "Suggested components:",
        columns: [
          {field: ReportRowField.LABEL, title: "Component"},
          {field: ReportRowField.VALUE, title: "Coverage"},
        ],
        rows: components.map(({name, covers}) => ({label: name, value: covers})),
        note,
      },
    ],
  };
};

const toCheckSummarySection = (
  proposals: ProposedCheck[],
  reportOnlyChecks: ReportOnlyCheck[],
  usage: AccountServicesUsage | undefined,
  p1Cells: string[]
): ReportDisplaySection => {
  const counts = new Map<MonitoringServiceType, number>();
  [...proposals, ...reportOnlyChecks].forEach(({monitoring_service_type}) => {
    counts.set(monitoring_service_type, (counts.get(monitoring_service_type) ?? 0) + 1);
  });
  const rows = groupTypesByTier([...counts.keys()], usage).flatMap(({label, types}) => {
    const typeRows = types.map((type) => ({label: CheckTypeDisplay[type], value: String(counts.get(type) ?? 0)}));
    if (!label) {
      return typeRows;
    }
    const count = types.reduce((sum, type) => sum + (counts.get(type) ?? 0), 0);
    return [...typeRows, {label: `Total ${label} Checks`, value: String(count), emphasis: ReportRowEmphasis.SUBTOTAL}];
  });
  const total = {
    label: "Total",
    value: String(proposals.length + reportOnlyChecks.length),
    emphasis: ReportRowEmphasis.TOTAL,
  };
  return {
    ...ANALYZER_REPORT_CHECK_SUMMARY_PRESET,
    rows: [],
    tables: [
      {
        variant: ReportTableVariant.SUMMARY,
        columns: [
          {field: ReportRowField.LABEL, title: "Type"},
          {field: ReportRowField.VALUE, title: "Count"},
        ],
        rows: [...rows, total],
        note:
          p1Cells.length > 0
            ? `Implementation should prioritize the P1 Checks immediately: ${conjunctionList.format(p1Cells)}.`
            : undefined,
      },
    ],
  };
};

const hasContent = ({intro, rows}: ReportSection) => Boolean(intro) || rows.length > 0;

export const orderReportRows = (
  proposals: ProposedCheck[],
  manualRows: ReportOnlyCheck[],
  usage: AccountServicesUsage | undefined,
  positionById: Map<string, number>,
  groups: ProposedGroup[] = []
): {proposals: ProposedCheck[]; groups: ProposedGroup[]; manualRows: ReportOnlyCheck[]; orderedIds: string[]} => {
  const positionOfProposal = ({id}: ProposedCheck) => positionById.get(id);
  const positionOfManualRow = (row: ReportOnlyCheck) => positionById.get(getManualRowId(manualRows.indexOf(row)));
  const orderedProposals = orderChecks(
    proposals.filter((check) => !isGroupCheck(check)),
    usage,
    positionOfProposal
  );
  const orderedManual = orderChecks(manualRows, usage, positionOfManualRow);
  const proposalIds = orderedProposals.map(({id}) => id);
  const manualIds = orderedManual.map((row) => getManualRowId(manualRows.indexOf(row)));
  const rowIds = [...proposalIds, ...manualIds];
  const rankOfGroup = ({member_ids, manual_rows}: ProposedGroup) => {
    const groupRowIds = [...member_ids, ...manual_rows.map(getManualRowId)];
    return rowIds.findIndex((rowId) => groupRowIds.includes(rowId));
  };
  const orderedGroups = [...groups].sort((left, right) => rankOfGroup(left) - rankOfGroup(right));
  const orderedIds = [
    ...proposalIds,
    ...orderedGroups.filter(({creatable}) => creatable).map(({id}) => id),
    ...manualIds,
    ...orderedGroups.filter(({creatable}) => !creatable).map(({id}) => id),
  ];
  return {proposals: orderedProposals, groups: orderedGroups, manualRows: orderedManual, orderedIds};
};

export const buildReportSections = (
  report: MonitoringReport,
  proposals: ProposedCheck[],
  user: AccountUser,
  usage: AccountServicesUsage | undefined,
  formData?: CheckFormResult,
  recommendedProposals: ProposedCheck[] = proposals,
  positions?: Map<string, number>,
  proposedGroups: ProposedGroup[] = []
): ReportDisplaySection[] => {
  const {
    domain,
    domain_overview,
    executive_summary,
    report_only_checks,
    type_rationales,
    sections,
    alert_note,
    probe_regions,
    probe_regions_reason,
    status_page,
  } = report;
  const ordered = orderReportRows(proposals, report_only_checks, usage, positions ?? new Map(), proposedGroups);
  const positionById = positions ?? assignPositions(new Map(), ordered.orderedIds);
  const groupProposals = orderChecks(proposals.filter(isGroupCheck), usage, ({id}) => positionById.get(id));
  const orderedProposals = [...ordered.proposals, ...groupProposals];
  const positionByCheck = new Map<ReportOnlyCheck, number>([
    ...orderedProposals.map((check): [ReportOnlyCheck, number] => [check, positionById.get(check.id) ?? 0]),
    ...ordered.manualRows.map((row): [ReportOnlyCheck, number] => [
      row,
      positionById.get(getManualRowId(report_only_checks.indexOf(row))) ?? 0,
    ]),
  ]);
  const groups = groupByBusinessFunction([...ordered.proposals, ...ordered.manualRows]);
  const manualGroupRows = ordered.groups
    .filter(({creatable}) => !creatable)
    .map((group) => {
      const rows = [
        ...ordered.proposals.filter(({id}) => group.member_ids.includes(id)),
        ...group.manual_rows.map((index) => report_only_checks[index]),
      ];
      const row = toGroupReportRow(group, rows, positionByCheck);
      positionByCheck.set(row, positionById.get(group.id) ?? 0);
      return row;
    });
  const reportOnlyChecks = [...ordered.manualRows, ...manualGroupRows];
  const priorityByCheck = toPriorityByCheck(domain, proposals, reportOnlyChecks);
  const p1Cells = toPriorityCells(groups, priorityByCheck, positionByCheck, AlertPriority.P1);
  return [
    {
      ...ANALYZER_REPORT_SUMMARY_PRESET,
      intro: [executive_summary, toCoverageSentence(proposals, reportOnlyChecks, sections)].join(" ").trim(),
      rows: [],
    },
    toOverviewSection(domain_overview, user),
    ...sections.filter(hasContent).map(toDisplaySection),
    {
      ...ANALYZER_REPORT_CHECKS_PRESET,
      rows: [],
      tables: toCheckTables(orderedProposals, type_rationales, positionByCheck),
    },
    ...toManualChecksSections(reportOnlyChecks, type_rationales, positionByCheck),
    toAlertSection(groups, priorityByCheck, positionByCheck, alert_note),
    toSensitivitySection(recommendedProposals, reportOnlyChecks, probe_regions, probe_regions_reason, formData),
    toStatusPageSection(status_page),
    toCheckSummarySection(proposals, reportOnlyChecks, usage, p1Cells),
    {...ANALYZER_REPORT_DISCLAIMER_PRESET, intro: ANALYZER_REPORT_DISCLAIMER, rows: []},
  ];
};

export const getReportTag = ({badge_status, confidence}: ReportRow): ReportTag | undefined => {
  if (badge_status) {
    return ANALYZER_REPORT_BADGE_DISPLAY[badge_status];
  }
  if (confidence === ReportConfidence.LIKELY) {
    return ANALYZER_REPORT_LIKELY_TAG;
  }
  return undefined;
};

const toPdfRow = ({badge_status, confidence, ...row}: ReportRow): ReportPdfRow => ({
  ...row,
  ...(confidence === ReportConfidence.LIKELY && {chip: ANALYZER_REPORT_LIKELY_TAG.label}),
  ...(badge_status && {badge: {label: ANALYZER_REPORT_BADGE_DISPLAY[badge_status].label, status: badge_status}}),
});

const toPdfTable = ({columns, rows, ...table}: ReportDisplayTable, sectionNumber?: string): ReportPdfTable => ({
  ...table,
  section_number: sectionNumber,
  columns: columns.map(({title}) => title),
  rows: rows.map((row) => {
    const tag = getReportTag(row);
    const cells = columns.map(({field}) => {
      if (field === ReportRowField.LABEL && tag) {
        return `${row.label} (${tag.label})`;
      }
      return String(row[field] ?? "");
    });
    return {cells, emphasis: row.emphasis};
  }),
});

export const buildReportPdfPayload = (
  report: MonitoringReport,
  proposals: ProposedCheck[],
  user: AccountUser,
  usage: AccountServicesUsage | undefined,
  formData?: CheckFormResult,
  recommendedProposals?: ProposedCheck[],
  positions?: Map<string, number>,
  proposedGroups?: ProposedGroup[]
): ReportPdfPayload => {
  const sections = buildReportSections(
    report,
    proposals,
    user,
    usage,
    formData,
    recommendedProposals,
    positions,
    proposedGroups
  );
  const numberedTitles = sections
    .map(({title}) => title)
    .filter((title) => !ANALYZER_PDF_UNNUMBERED_TITLES.includes(title));
  return {
    title: ANALYZER_REPORT_TITLE,
    domain: report.domain,
    sections: sections.map(({title, intro, link, rows, tables}) => {
      const numberedIndex = numberedTitles.indexOf(title);
      const sectionNumber = numberedIndex === -1 ? undefined : String(numberedIndex + 1);
      const hasNumberedTables =
        sectionNumber !== undefined &&
        [ANALYZER_REPORT_CHECKS_PRESET.title, ANALYZER_REPORT_MANUAL_CHECKS_PRESET.title].includes(title);
      return {
        section_number: sectionNumber && `${sectionNumber}.`,
        title,
        intro,
        link: link && {label: link.label, href: `${window.location.origin}${link.to}`},
        rows: rows.map(toPdfRow),
        tables: tables?.map((table, index) =>
          toPdfTable(table, hasNumberedTables ? `${sectionNumber}.${index + 1}` : undefined)
        ),
      };
    }),
  };
};

export const getTurnType = (body: SSEStreamBody): AnalyzerTurnType => {
  if (!body.thread_id) {
    return AnalyzerTurnType.INITIAL;
  }
  if (body.action === SSERespondAction.REVISE) {
    return AnalyzerTurnType.REVISE;
  }
  if (body.answer !== undefined) {
    return AnalyzerTurnType.INTERVIEW_ANSWER;
  }
  return AnalyzerTurnType.RETRY;
};

export const logTrackingFailure = (error: unknown) => {
  console.error("Assistant run tracking failed", error);
  captureException(error);
};

export const logTrackingRejection = ({data}: FetchResult<GenericMutationResponse>) => {
  const response = data?.response;
  if (response && response.status !== ResponseStatus.SUCCESS) {
    logTrackingFailure(new Error(`Assistant run tracking rejected: ${JSON.stringify(response.message)}`));
  }
};

export const guardTracking =
  <Args extends unknown[]>(track: (...args: Args) => void) =>
  (...args: Args) => {
    try {
      track(...args);
    } catch (error) {
      logTrackingFailure(error);
    }
  };

export const toUsageInput = ({
  input_tokens,
  output_tokens,
  cache_read_tokens,
  cache_write_tokens,
  pricing,
}: SSETokenUsage): AnalyzerTokenUsageInput => ({
  input_tokens,
  output_tokens,
  ...(cache_read_tokens !== undefined && {cache_read_tokens}),
  ...(cache_write_tokens !== undefined && {cache_write_tokens}),
  ...(pricing && {pricing}),
});
